import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Tracks which document export job ids the current user has already clicked Download on from
 * *this* browser. Backed by localStorage so the "Downloaded" marker survives tab reloads and
 * returning to the app the next day. This state is intentionally per-device — there is no
 * server-side tracking — and is cleared naturally as exports expire (7 days) or are removed
 * from the list.
 *
 * <p>All three download surfaces in the app write to this service on a successful iframe
 * handoff:
 * <ul>
 *   <li>{@code SharedExportModalComponent} (inline auto-download after polling completes)</li>
 *   <li>{@code CurrentExportsPanelComponent} (manual click)</li>
 *   <li>{@code ExportDownloadComponent} (email-link landing page)</li>
 * </ul>
 */
@Injectable({ providedIn: 'root' })
export class DownloadedExportsService {

  private readonly STORAGE_KEY = 'oes.downloaded-exports';
  private readonly downloadedIdsSubject = new BehaviorSubject<Set<string>>(this.loadFromStorage());

  /**
   * Emits a fresh snapshot of the downloaded-id set whenever the set changes. Consumers can
   * subscribe to update UI reactively without re-fetching.
   */
  readonly downloadedIds$: Observable<Set<string>> = this.downloadedIdsSubject.asObservable();

  markDownloaded(jobId: string): void {
    if (!jobId) {
      return;
    }
    const current = this.downloadedIdsSubject.value;
    if (current.has(jobId)) {
      return;
    }
    const next = new Set(current);
    next.add(jobId);
    this.saveToStorage(next);
    this.downloadedIdsSubject.next(next);
  }

  isDownloaded(jobId: string): boolean {
    return this.downloadedIdsSubject.value.has(jobId);
  }

  /**
   * Drop any stored ids that no longer appear in the current live-jobs list. Optional cleanup
   * called by the Current Exports panel when it refreshes — expired or soft-deleted rows
   * leave orphaned ids behind that are otherwise harmless but add up over time.
   */
  pruneToLiveJobs(liveJobIds: Iterable<string>): void {
    const live = new Set(liveJobIds);
    const current = this.downloadedIdsSubject.value;
    const pruned = new Set<string>();
    let changed = false;
    current.forEach((id) => {
      if (live.has(id)) {
        pruned.add(id);
      } else {
        changed = true;
      }
    });
    if (changed) {
      this.saveToStorage(pruned);
      this.downloadedIdsSubject.next(pruned);
    }
  }

  private loadFromStorage(): Set<string> {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) {
        return new Set();
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? new Set<string>(parsed.filter((v) => typeof v === 'string')) : new Set();
    } catch {
      return new Set();
    }
  }

  private saveToStorage(ids: Set<string>): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(Array.from(ids)));
    } catch {
      // Quota or disabled storage — safe to ignore; in-memory state still correct for session.
    }
  }
}
